import {
  SERVER_TO_SERVER_USER_AGENT,
  SERVER_TO_SERVER_FETCH_SECRET,
} from '../../pages/api/utils/config'
import {
  HOST,
  DEBUG_MODE,
  FORCE_NO_PLAYLIST_EDITS_PRACTICAL_POD,
  GLOBAL_RAW_ENTRIES_ARTISTS_AND_GENRECONTRACTS_FETCH_AND_RESPONSE_CACHE_REVALIDATE_MINUTES,
} from './config'
import {
  cureCDN,
  cureArweave,
  getMintDate,
  spaceEscapes,
  formatDuration,
  toTrimmedString,
  CURE_CDN_JPG_EXT,
  CURE_CDN_AVIF_EXT,
  formatSongNameBig,
  isNullOrEmptyString,
  formatSongNameSmall,
  formatArtistNameBig,
  formatSongNameBigger,
  formatArtistNameBigger,
  isNotNullOrEmptyString,
  cureArweaveOld,
} from './misc'

export async function getAllBulkLicensedEntries() {
  return new Promise<any>(async (resolve) => {
    try {
      const opts: any = {
        method: 'GET',
        next: {
          revalidate:
            GLOBAL_RAW_ENTRIES_ARTISTS_AND_GENRECONTRACTS_FETCH_AND_RESPONSE_CACHE_REVALIDATE_MINUTES *
            60,
        },
      }
      await fetch(HOST + '/api/p.stream-sv/e/gec', opts)
        .then(async (x: any) => {
          let ret: any = await x.json()
          if (ret?.entries?.length > 0)
            ret?.entries?.map((e: any, i: any) => {
              if (ret?.entries[i])
                ret.entries[i].genres =
                  e?.genres?.length > 0 ? e?.genres?.split(',').map(Number) : []
            })
          resolve(ret?.entries?.length > 0 ? ret?.entries : [])
        })
        .catch((er: any) => {
          if (DEBUG_MODE) console.log(er)
          resolve([])
        })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve([])
    }
  })
}

export async function getSomeEntries() {
  return new Promise<any>(async (resolve) => {
    try {
      await fetch(HOST + '/api/p.stream-sv/e/gs', {
        method: 'POST',
        headers: {
          'x-server-secret': SERVER_TO_SERVER_FETCH_SECRET,
          'User-Agent': SERVER_TO_SERVER_USER_AGENT,
        },
      })
        .then(async (x: any) => {
          let ret: any = await x.json()
          if (ret?.entries?.length > 0)
            ret?.entries?.map((e: any, i: any) => {
              if (ret?.entries[i])
                ret.entries[i].genres =
                  e?.genres?.length > 0 ? e?.genres?.split(',').map(Number) : []
            })
          resolve(ret?.entries?.length > 0 ? ret?.entries : [])
        })
        .catch((er: any) => {
          if (DEBUG_MODE) console.log(er)
          resolve([])
        })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve([])
    }
  })
}

const yieldToMain = () => {
  return globalThis.scheduler?.yield?.() || new Promise((r) => setTimeout(r, 0))
}

export async function mergeSongsWithSuppliedEntries(
  newSongs: any,
  allEntries: any,
  avifCompatible: boolean,
  loopThroughNewSongs: boolean,
  isPracticalPod: boolean,
) {
  return new Promise<any>(async (resolve) => {
    try {
      let lastYieldTime = performance.now()
      let mergedSongs: any = []
      let extSong: any = null
      if (loopThroughNewSongs) {
        const entriesLookup = new Map(
          allEntries?.map((en: any) => [
            `${en.contract_address}_${en.token_id}`,
            en,
          ]),
        )
        newSongs.map(async (cln: any, i: any): Promise<any> => {
          const compositeKey = `${cln?.contract_address}_${cln?.token_id}`
          const matchingEntry = entriesLookup.get(compositeKey)
          extSong = mergeEntryProperties(
            matchingEntry,
            cln,
            i,
            avifCompatible,
            isPracticalPod,
          )
          if (extSong) mergedSongs.push(extSong)
          if (performance.now() - lastYieldTime > 50) {
            await yieldToMain()
            lastYieldTime = performance.now()
          }
        })
      } else {
        const songsLookup = new Map(
          newSongs?.map((en: any) => [
            `${en.contract_address}_${en.token_id}`,
            en,
          ]),
        )
        allEntries.map(async (en: any, i: any): Promise<any> => {
          const compositeKey = `${en?.contract_address}_${en?.token_id}`
          const matchingSong = songsLookup.get(compositeKey)
          extSong = mergeSongProperties(
            matchingSong,
            en,
            i,
            avifCompatible,
            isPracticalPod,
          )
          if (extSong) mergedSongs.push(extSong)
          if (performance.now() - lastYieldTime > 50) {
            await yieldToMain()
            lastYieldTime = performance.now()
          }
        })
      }
      allEntries = undefined
      newSongs = undefined
      resolve(mergedSongs?.length > 0 ? mergedSongs : [])
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve([])
    }
  })
}

function mergeSongProperties(
  song: any,
  entry: any,
  idx: any,
  avifCompatible: boolean,
  isPracticalPod: boolean,
) {
  if (!song || !entry) return null
  try {
    song.frontImage =
      !avifCompatible || isNullOrEmptyString(song?.front_image_avif)
        ? cureArweaveOld(song?.front_image, CURE_CDN_JPG_EXT)
        : cureArweaveOld(song?.front_image_avif, CURE_CDN_AVIF_EXT)
    song.av = avifCompatible && isNotNullOrEmptyString(song?.front_image_avif)
    song.frontImageCD =
      !avifCompatible || isNullOrEmptyString(song?.front_image_avif)
        ? cureCDN(song?.front_image, CURE_CDN_JPG_EXT, false)
        : cureCDN(song?.front_image_avif, CURE_CDN_AVIF_EXT, false)
    song.duration = entry?.duration ? entry?.duration : null
    song.formattedDuration = formatDuration(entry?.duration, false)
    song.formattedArtistNameBig = formatArtistNameBig(song?.artist)
    song.formattedArtistNameBigger = formatArtistNameBigger(song?.artist)
    song.formattedSongNameSmallSpaceEscaped = formatSongNameSmall(
      spaceEscapes(song?.name),
    )
    song.formattedSongNameBig = formatSongNameBig(song?.name)
    song.formattedSongNameBigger = formatSongNameBigger(song?.name)
    song.formattedMintDate = isNotNullOrEmptyString(song?.formattedMintDate)
      ? song?.formattedMintDate
      : getMintDate(song?.mint_date)
    song.genres =
      entry?.genres && entry?.genres?.length > 0 ? entry?.genres : []
    song.favorited = false
    const trimmedIdx = toTrimmedString(idx)
    song.keyTwo = trimmedIdx + 's2'
    song.keyOne = trimmedIdx + 's1'
    song.keyThree = trimmedIdx + 's3'
    delete song.mint_date
    delete song.front_image
    delete song.front_image_avif
    delete song.isCompressed
    if (isPracticalPod && FORCE_NO_PLAYLIST_EDITS_PRACTICAL_POD)
      delete song.formattedSongNameSmallSpaceEscaped
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return song
}

function mergeEntryProperties(
  entry: any,
  song: any,
  idx: any,
  avifCompatible: boolean,
  isPracticalPod: boolean,
) {
  if (!song || !entry) return null
  try {
    song.frontImage =
      !avifCompatible || isNullOrEmptyString(song?.front_image_avif)
        ? cureArweave(song?.front_image)
        : cureArweave(song?.front_image_avif)
    song.av = avifCompatible && isNotNullOrEmptyString(song?.front_image_avif)
    song.frontImageCD =
      !avifCompatible || isNullOrEmptyString(song?.front_image_avif)
        ? cureCDN(song?.front_image, CURE_CDN_JPG_EXT, false)
        : cureCDN(song?.front_image_avif, CURE_CDN_AVIF_EXT, false)
    song.duration = entry?.duration ? entry?.duration : null
    song.formattedDuration = formatDuration(entry?.duration, false)
    song.formattedArtistNameBig = formatArtistNameBig(song?.artist)
    song.formattedArtistNameBigger = formatArtistNameBigger(song?.artist)
    song.formattedSongNameSmallSpaceEscaped = formatSongNameSmall(
      spaceEscapes(song?.name),
    )
    song.formattedSongNameBig = formatSongNameBig(song?.name)
    song.formattedSongNameBigger = formatSongNameBigger(song?.name)
    song.formattedMintDate = isNotNullOrEmptyString(song?.formattedMintDate)
      ? song?.formattedMintDate
      : getMintDate(song?.mint_date)
    song.genres =
      entry?.genres && entry?.genres?.length > 0 ? entry?.genres : []
    song.favorited = false
    const trimmedIdx = toTrimmedString(idx)
    song.keyOne = trimmedIdx + '_1'
    song.keyTwo = trimmedIdx + '_2'
    song.keyThree = trimmedIdx + '_3'
    delete song.mint_date
    delete song.front_image
    delete song.front_image_avif
    delete song.isCompressed
    if (isPracticalPod && FORCE_NO_PLAYLIST_EDITS_PRACTICAL_POD)
      delete song.formattedSongNameSmallSpaceEscaped
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return song
}

export function shuffleSongs(songs: any) {
  if (!songs || songs.length === 0) return []
  for (let i = songs.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = songs[i]
    songs[i] = songs[j]
    songs[j] = temp
  }
  return songs
}
